import {
  BaseFieldFilter,
  CustomFieldFilter,
  NativeFieldFilter,
  SimpleSearchFieldFilter,
} from 'api/dashboard/widget';
import {
  AssigneeField,
  BooleanField,
  DateField,
  EstimateField,
  FieldType,
  FilterableField,
  MultiSelectField,
  MultiUserField,
  NumberField,
  SingleSelectField,
  SingleUserField,
  SourceType,
  TextField,
} from 'components/organisms/tasks/fields';
import { FieldFilter } from 'components/organisms/tasks/filters';
import type { AssigneeFieldFilter } from 'components/organisms/tasks/filters/assignee';
import type { BooleanFieldFilter } from 'components/organisms/tasks/filters/boolean';
import type { DateFieldFilter } from 'components/organisms/tasks/filters/date';
import type { EstimateFieldFilter } from 'components/organisms/tasks/filters/estimate';
import type { MultiSelectFieldFilter } from 'components/organisms/tasks/filters/multi-select';
import type { MultiUserFieldFilter } from 'components/organisms/tasks/filters/multi-user';
import type { NumberFieldFilter } from 'components/organisms/tasks/filters/number';
import { Operation } from 'components/organisms/tasks/filters/operations';
import { GroupSeparator } from 'components/organisms/tasks/filters/separators';
import type { SingleSelectFieldFilter } from 'components/organisms/tasks/filters/single-select';
import type { SingleUserFieldFilter } from 'components/organisms/tasks/filters/single-user';
import type { TextFieldFilter } from 'components/organisms/tasks/filters/text';
import type { GroupCriteria } from 'components/organisms/tasks/grouped-filter-popover';
import { unreachable } from '../unreachable';
import { AssigneeFilter } from './fields/assignee';
import { BooleanFilter } from './fields/boolean';
import { DateFilter } from './fields/date';
import { EstimateFilter } from './fields/estimate';
import { MultiSelectFilter } from './fields/multi-select';
import { MultiUserFilter } from './fields/multi-user';
import { NumberFilter } from './fields/number';
import { SingleSelectFilter } from './fields/single-select';
import { SingleUserFilter } from './fields/single-user';
import { TextFilter } from './fields/text';

export const toApiFormat = (filter: FieldFilter): BaseFieldFilter => {
  switch (filter.field.type) {
    case FieldType.Boolean:
      return BooleanFilter.toApiFormat(filter as BooleanFieldFilter);
    case FieldType.Number:
      return NumberFilter.toApiFormat(filter as NumberFieldFilter);
    case FieldType.Date:
      return DateFilter.toApiFormat(filter as DateFieldFilter);
    case FieldType.Estimate:
      return EstimateFilter.toApiFormat(filter as EstimateFieldFilter);
    case FieldType.Text:
    case FieldType.MultiLineText:
    case FieldType.Note:
      return TextFilter.toApiFormat(filter as TextFieldFilter);
    case FieldType.SingleSelect:
      return SingleSelectFilter.toApiFormat(filter as SingleSelectFieldFilter);
    case FieldType.MultiSelect:
      return MultiSelectFilter.toApiFormat(filter as MultiSelectFieldFilter);
    case FieldType.SingleUser:
      return SingleUserFilter.toApiFormat(filter as SingleUserFieldFilter);
    case FieldType.MultiUser:
      return MultiUserFilter.toApiFormat(filter as MultiUserFieldFilter);
    case FieldType.Assignee:
      return AssigneeFilter.toApiFormat(filter as AssigneeFieldFilter);
    default:
      return unreachable(filter.field);
  }
};

export const toSheetApiFormat = (filter: FieldFilter): BaseFieldFilter => {
  switch (filter.field.type) {
    case FieldType.Boolean:
      return BooleanFilter.toApiFormat(filter as BooleanFieldFilter);
    case FieldType.Number:
      return NumberFilter.toApiFormat(filter as NumberFieldFilter);
    case FieldType.Date:
      return DateFilter.toApiFormat(filter as DateFieldFilter);
    case FieldType.Estimate:
      return EstimateFilter.toApiFormat(filter as EstimateFieldFilter);
    case FieldType.Text:
    case FieldType.MultiLineText:
    case FieldType.Note:
      return TextFilter.toApiFormat(filter as TextFieldFilter);
    case FieldType.SingleSelect:
      return SingleSelectFilter.toApiFormat(filter as SingleSelectFieldFilter);
    case FieldType.MultiSelect:
      return MultiSelectFilter.toApiFormat(filter as MultiSelectFieldFilter);
    case FieldType.SingleUser:
      return SingleUserFilter.toApiFormat(filter as SingleUserFieldFilter);
    case FieldType.MultiUser:
      return MultiUserFilter.toApiFormat(filter as MultiUserFieldFilter);
    case FieldType.Assignee:
      return AssigneeFilter.toApiFormat(filter as AssigneeFieldFilter);
    default:
      return toApiFormat(filter);
  }
};

const parse = (
  filter: SimpleSearchFieldFilter,
  field: FilterableField,
): FieldFilter => {
  const fieldType = field.type;

  switch (fieldType) {
    case FieldType.Boolean:
      return BooleanFilter.parse(
        filter as NativeFieldFilter,
        field as BooleanField,
      );
    case FieldType.Number:
      return NumberFilter.parse(
        filter as NativeFieldFilter,
        field as NumberField,
      );
    case FieldType.Date:
      return DateFilter.parse(filter as NativeFieldFilter, field as DateField);
    case FieldType.Text:
    case FieldType.MultiLineText:
    case FieldType.Note:
      return TextFilter.parse(filter as NativeFieldFilter, field as TextField);
    case FieldType.SingleSelect:
      return SingleSelectFilter.parse(
        filter as NativeFieldFilter,
        field as SingleSelectField,
      );
    case FieldType.MultiSelect:
      return MultiSelectFilter.parse(
        filter as NativeFieldFilter,
        field as MultiSelectField,
      );
    case FieldType.SingleUser:
      return SingleUserFilter.parse(
        filter as NativeFieldFilter,
        field as SingleUserField,
      );
    case FieldType.MultiUser:
      return MultiUserFilter.parse(
        filter as NativeFieldFilter,
        field as MultiUserField,
      );
    case FieldType.Estimate:
      return EstimateFilter.parse(
        filter as NativeFieldFilter,
        field as EstimateField,
      );
    case FieldType.Assignee:
      return AssigneeFilter.parse(
        filter as NativeFieldFilter,
        field as AssigneeField,
      );
    default:
      return unreachable(fieldType);
  }
};

const toNativeFieldFilterFormat = (filter: FieldFilter): NativeFieldFilter => {
  return {
    name: filter.field.fieldName,
    ...toApiFormat(filter),
  };
};

const toCustomFieldFilterFormat = (filter: FieldFilter): CustomFieldFilter => {
  return {
    fieldId: filter.field.fieldId,
    ...toApiFormat(filter),
  };
};

const toFieldFilterFromObject = (obj: {
  [key: string]: string;
}): SimpleSearchFieldFilter[] => {
  const validKeys = Object.keys(obj).filter((key) => key.includes('*'));

  const fieldFilters = validKeys.map((key) => {
    const [sourceType, restStr] = key.split('*');
    const queryDataArr = restStr.split('.');
    const operation = queryDataArr.pop() as Operation;
    const name = queryDataArr.pop() as string;
    const value = obj[key];

    return {
      sourceType: sourceType as SourceType,
      operation,
      name,
      fieldId: queryDataArr.length > 0 ? Number(name) : undefined,
      value,
    };
  });

  return fieldFilters;
};

const parseFromObject = (
  obj: {
    [key: string]: string;
  },
  fields: FilterableField[],
) => {
  const searchFields = toFieldFilterFromObject(obj);
  const filteredField = searchFields.map(
    (currField) =>
      fields.find(
        (fld) =>
          fld.fieldName === (currField as NativeFieldFilter).name ||
          fld.fieldId === (currField as CustomFieldFilter).fieldId,
      ) as FilterableField,
  );

  return searchFields.map((currField, index) =>
    parse(currField, filteredField[index] || {}),
  );
};

const getValueFromFilter = (
  filter: NativeFieldFilter | CustomFieldFilter,
  encode = true,
) => {
  switch (filter.operation) {
    case Operation.IsEmpty:
    case Operation.IsNotEmpty:
    case Operation.IsNull:
      return true;
    default:
      return filter.value
        ? encode
          ? encodeURIComponent(filter.value)
          : filter.value
        : undefined;
  }
};

const nativeFieldToQueryStringFormat = (field: NativeFieldFilter): string =>
  `${field.key ? `${field.key}${GroupSeparator}` : ''}${
    field.sourceType
  }${GroupSeparator}${field.name}.${field.operation}=${getValueFromFilter(
    field,
  )}`;

const nativeFieltdToQueryRecordFormat = (field: NativeFieldFilter) => {
  const key = `${field.key ? `${field.key}${GroupSeparator}` : ''}${
    field.sourceType
  }${GroupSeparator}${field.name}.${field.operation}`;
  const value = getValueFromFilter(field, false);

  return [key, value] as const;
};

export const customFieldKeyToString = (field: {
  sourceType: SourceType;
  fieldId: string | number;
  key?: string;
}): string =>
  `${field.key ? `${field.key}${GroupSeparator}` : ''}${
    field.sourceType
  }${GroupSeparator}field.${field.fieldId}`;

const customFieldToQueryStringFormat = (field: CustomFieldFilter): string =>
  `${customFieldKeyToString(field)}.${field.operation}=${getValueFromFilter(
    field,
  )}`;

const customFieldToQueryRecordFormat = (field: CustomFieldFilter) => {
  const key = `${customFieldKeyToString(field)}.${field.operation}`;
  const value = getValueFromFilter(field);

  return [key, value] as const;
};

const toQueryStringFormat = (filters: FieldFilter[]): string =>
  [
    ...filters
      .filter((x) => !x.field.userCreated)
      .map(toNativeFieldFilterFormat)
      .map(nativeFieldToQueryStringFormat),
    ...filters
      .filter((x) => x.field.userCreated)
      .map(toCustomFieldFilterFormat)
      .map(customFieldToQueryStringFormat),
  ].join('&');

const convertQueryStringToObject = (
  filterString: string,
): { [key: string]: string } => {
  const filterObject = {};

  filterString.split('&').forEach((x) => {
    const [key, value] = decodeURIComponent(x).split('=');

    if (Object.keys(filterObject).includes(key)) {
      if (!Array.isArray(filterObject[key])) {
        filterObject[key] = [filterObject[key], value];
      } else {
        filterObject[key] = [...filterObject[key], value];
      }
    } else {
      filterObject[key] = value;
    }
  });

  return filterObject;
};

export const ApiFilter = {
  toNativeFieldFilterFormat,
  toCustomFieldFilterFormat,
  parse,
  parseFromObject,
  toQueryStringFormat,
  nativeFieldToQueryStringFormat,
  customFieldToQueryStringFormat,
  convertQueryStringToObject,
};

const toNativeFieldFilterFormatForGroups = (
  filter: GroupCriteria,
): NativeFieldFilter => {
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  const apiFormat = toApiFormat(filter.criteria);

  // Transform sourceType to snake_case for API / query-string compatibility (saved views, etc.)
  const sourceType =
    apiFormat.sourceType === SourceType.RevenueEntry
      ? 'revenue_entry'
      : apiFormat.sourceType === SourceType.ProjectFinancialBudget
      ? 'project_financials_budget'
      : apiFormat.sourceType === SourceType.ResourceAllocation
      ? 'resource_allocation'
      : apiFormat.sourceType;

  return {
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    name: filter.criteria.field.fieldName,
    ...(filter.key
      ? {
          key: filter.key,
        }
      : {}),
    ...apiFormat,
    ...(sourceType ? { sourceType: sourceType as SourceType } : {}),
  } as NativeFieldFilter;
};

const toCustomFieldFilterFormatForGroups = (
  filter: GroupCriteria,
): CustomFieldFilter => {
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  const apiFormat = toApiFormat(filter.criteria);

  // Transform sourceType to snake_case for API / query-string compatibility (saved views, etc.)
  const sourceType =
    apiFormat.sourceType === SourceType.RevenueEntry
      ? 'revenue_entry'
      : apiFormat.sourceType === SourceType.ProjectFinancialBudget
      ? 'project_financials_budget'
      : apiFormat.sourceType === SourceType.ResourceAllocation
      ? 'resource_allocation'
      : apiFormat.sourceType;

  return {
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    fieldId: filter.criteria.field.fieldId,
    ...(filter.key
      ? {
          key: filter.key,
        }
      : {}),
    ...apiFormat,
    ...(sourceType ? { sourceType: sourceType as SourceType } : {}),
  } as CustomFieldFilter;
};

const filterGroupCriteriaBySourceType = (
  filters: GroupCriteria[],
  sourceType: SourceType,
): GroupCriteria[] =>
  filters.filter(
    (x) =>
      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      FieldFilter.isValid(x.criteria) &&
      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      x.criteria.field.sourceType === sourceType,
  );

const toQueryStringFormatForGroups = (filters: GroupCriteria[]): string =>
  [
    ...filters
      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      .filter((x) => !x.criteria.field.userCreated)
      .map(toNativeFieldFilterFormatForGroups)
      .map(nativeFieldToQueryStringFormat),
    ...filters
      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      .filter((x) => x.criteria.field.userCreated)
      .map(toCustomFieldFilterFormatForGroups)
      .map(customFieldToQueryStringFormat),
  ].join('&');

const toQueryStringFormatForGroupsForSourceType = (
  filters: GroupCriteria[],
  sourceType: SourceType,
): string =>
  toQueryStringFormatForGroups(
    filterGroupCriteriaBySourceType(filters, sourceType),
  );

const toQueryRecordFormatForGroups = (
  filters: GroupCriteria[],
): Record<string, string> =>
  filters.reduce((acc, filter) => {
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    if (!filter.criteria.field.userCreated) {
      // Native
      const currentFilter = toNativeFieldFilterFormatForGroups(filter);
      const [k, v] = nativeFieltdToQueryRecordFormat(currentFilter);

      return { ...acc, [k]: v };
    }

    // Custom
    const currentFilter = toCustomFieldFilterFormatForGroups(filter);
    const [k, v] = customFieldToQueryRecordFormat(currentFilter);

    return { ...acc, [k]: v };
  }, {});

export const ApiGroupFilter = {
  toNativeFieldFilterFormatForGroups,
  toCustomFieldFilterFormatForGroups,
  toQueryStringFormatForGroups,
  toQueryStringFormatForGroupsForSourceType,
  toQueryRecordFormatForGroups,
};
